import React from 'react';
import { Modal } from './Modal';
import { Input } from '../ui/input';
import { useAddContentForm } from '@/hooks/useAddContentForm';

const ALLOWED_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/ogg',
  'audio/aac',
  'audio/x-aac',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
];

const ALLOWED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.aac', '.m4a'];

interface AddContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId?: number;
  onSuccess?: () => void;
  isLocked?: boolean;
}

export const AddContentModal = React.memo(
  ({ isOpen, onClose, categoryId, onSuccess, isLocked }: AddContentModalProps) => {
    const {
      currentStep,
      title,
      description,
      embedUrl,
      image,
      file,
      isDragging,
      errors,
      touched,
      mantra,
      videoFile,
      mantraTranslation,
      mantraPronunciation,
      product,
      personalProduct,
      mantraIcon,
      isSubmittingAudio,
      fileInputRef,
      mantraIconInputRef,
      isLockedFlow,
      isMantraStep,
      isPrimaryDisabled,
      handleTitleChange,
      handleDescriptionChange,
      handleFileChange,
      handleVideoFileChange,
      handleEmbedUrlChange,
      handleImageChange,
      handleMantraChange,
      handleMantraTranslationChange,
      handleMantraPronunciationChange,
      handleMantraIconChange,
      handleProductChange,
      handlePersonalProductChange,
      handleRemoveMantraIcon,
      clearImage,
      clearSelection,
      setIsDragging,
      setTouched,
      setErrors,
      handleAudioSelection,
      handleSecondaryAction,
      handlePrimaryAction,
    } = useAddContentForm({
      isOpen,
      onClose,
      categoryId,
      onSuccess,
      isLocked,
    });

    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Add New Content">
        <div className="space-y-5">
          {isLockedFlow && (
            <div className="flex items-center justify-center gap-3">
              <div
                className={`px-4 py-2 rounded-xl text-sm font-semibold border ${
                  currentStep === 1
                    ? 'bg-[#1A0A30] text-white border-[#1A0A30]'
                    : 'bg-[#F8F5FA] text-[#62556B] border-[#E7DFF0]'
                }`}
              >
                Step 1
              </div>
              <div className="w-8 h-px bg-[#D8CBE6]" />
              <div
                className={`px-4 py-2 rounded-xl text-sm font-semibold border ${
                  currentStep === 2
                    ? 'bg-[#1A0A30] text-white border-[#1A0A30]'
                    : 'bg-[#F8F5FA] text-[#62556B] border-[#E7DFF0]'
                }`}
              >
                Step 2
              </div>
            </div>
          )}

          {!isMantraStep && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">Title *</label>
                <input
                  type="text"
                  placeholder="Enter title of the Mantra"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full px-4 py-3 bg-[#F8F5FA] rounded-xl text-[15px] focus:outline-none"
                />
                {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
              </div>
              {!isLocked && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#62556B]">Description *</label>
                  <textarea
                    value={description}
                    placeholder="Write description here..."
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 bg-[#F8F5FA] rounded-xl text-[15px] resize-none focus:outline-none"
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs">{errors.description}</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">Upload Audio File *</label>

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);

                    const droppedFile = e.dataTransfer.files?.[0];
                    if (!droppedFile) return;

                    const isValidType =
                      !droppedFile.type || ALLOWED_AUDIO_MIME_TYPES.includes(droppedFile.type);
                    const isValidExtension = ALLOWED_AUDIO_EXTENSIONS.some((ext) =>
                      droppedFile.name.toLowerCase().endsWith(ext)
                    );

                    if (!isValidType || !isValidExtension) {
                      setTouched((prev) => ({ ...prev, file: true }));
                      setErrors((prev) => ({
                        ...prev,
                        file: 'Only MP3, WAV, OGG, AAC, or M4A audio files are allowed',
                      }));
                      return;
                    }

                    setTouched((prev) => ({ ...prev, file: true }));
                    void handleAudioSelection(droppedFile);
                  }}
                  className={`border-2 border-dashed rounded-lg p-6 flex items-center justify-center transition-all
              ${isDragging ? 'border-[#9275C9] bg-[#EFEAF3]' : 'border-[#A17DBE] bg-[#F8F5FA]'}`}
                >
                  {file ? (
                    <div className="flex items-center gap-2">
                      <span className="px-4 py-2 bg-white border rounded-lg text-sm truncate max-w-[220px]">
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="text-red-500 font-bold"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer text-sm font-medium text-[#726779]">
                      <p className="text-center px-4 py-3 rounded-lg border-[1px] border-lightPurple">
                        Choose audio file
                      </p>
                      <Input
                        type="file"
                        accept=".mp3,.wav,.ogg,.aac,.m4a"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                  )}
                </div>

                {(touched.file || touched.embedUrl || touched.submitAttempted) && errors.file && (
                  <p className="text-red-500 text-xs mt-1">{errors.file}</p>
                )}
              </div>
              {!isLockedFlow && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#62556B]">Or Upload Video</label>

                  <div className="w-full border-2 border-dashed rounded-lg p-6 flex items-center justify-center transition-all border-[#A17DBE] bg-[#F8F5FA]">
                    {videoFile ? (
                      <div className="flex items-center gap-2">
                        <span className="px-4 py-2 bg-white border rounded-lg text-sm truncate max-w-[220px]">
                          {videoFile.name}
                        </span>

                        <button
                          type="button"
                          onClick={clearSelection}
                          className="text-red-500 font-bold"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer text-sm font-medium text-[#726779]">
                        <p className="text-center px-4 py-3 rounded-lg border border-lightPurple">
                          Choose video
                        </p>

                        <Input
                          type="file"
                          accept=".mp4,.webm,.ogg,.m4v,video/*"
                          className="hidden"
                          onChange={handleVideoFileChange}
                        />
                      </label>
                    )}
                  </div>

                  {errors.videoFile && <p className="text-red-500 text-xs">{errors.videoFile}</p>}
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">Or Paste Embed URL</label>

                <input
                  type="text"
                  placeholder="https://example.com/embed/audio"
                  value={embedUrl}
                  onChange={(e) => handleEmbedUrlChange(e.target.value)}
                  className="w-full px-4 py-3 bg-[#F8F5FA] rounded-xl text-[15px] focus:outline-none"
                />

                {errors.embedUrl && <p className="text-red-500 text-xs mt-1">{errors.embedUrl}</p>}
              </div>

              <div className="space-y-2 flex flex-col">
                <label className="text-sm font-semibold text-[#62556B] text-start">
                  Upload Thumbnail Image
                </label>

                <div className="w-full border-2 border-dashed rounded-lg p-6 flex items-center justify-center transition-all border-[#9275C9] bg-[#EFEAF3]">
                  {image ? (
                    <div className="flex items-center gap-3">
                      <span className="px-4 py-2 bg-white border rounded-lg text-sm truncate max-w-[220px]">
                        {image.name}
                      </span>

                      <button
                        type="button"
                        onClick={clearImage}
                        className="text-red-500 font-bold hover:opacity-70"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer text-sm font-medium text-[#726779]">
                      <p className="text-center px-4 py-3 rounded-lg border-[1px] border-lightPurple">
                        Choose image
                      </p>
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageChange(e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>

                {errors.image && <p className="text-red-500 text-xs">{errors.image}</p>}
              </div>
            </>
          )}

          {isMantraStep && (
            <div className="p-4 border border-purple-100 bg-purple-50/30 rounded-xl space-y-4">
              <h3 className="font-bold text-[#1A0A30]">Mantra Details (Locked Content)</h3>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">
                  Mantra (Sanskrit/Original) *
                </label>
                <textarea
                  placeholder="Enter mantra"
                  value={mantra}
                  onChange={(e) => handleMantraChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none resize-none"
                  rows={3}
                />
                {(touched.mantra || touched.submitAttempted) && errors.mantra && (
                  <p className="text-red-500 text-xs">{errors.mantra}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">
                  Mantra Translation (English) *
                </label>
                <textarea
                  placeholder="Enter translation"
                  value={mantraTranslation}
                  onChange={(e) => handleMantraTranslationChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none resize-none"
                  rows={3}
                />
                {(touched.mantraTranslation || touched.submitAttempted) &&
                  errors.mantraTranslation && (
                    <p className="text-red-500 text-xs">{errors.mantraTranslation}</p>
                  )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">
                  Mantra Pronunciation (English) *
                </label>
                <textarea
                  placeholder="Enter pronunciation (e.g., Om Bhadram...)"
                  value={mantraPronunciation}
                  onChange={(e) => handleMantraPronunciationChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none resize-none"
                  rows={3}
                />
                {(touched.mantraPronunciation || touched.submitAttempted) &&
                  errors.mantraPronunciation && (
                    <p className="text-red-500 text-xs">{errors.mantraPronunciation}</p>
                  )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">Add Product link</label>
                <input
                  type="text"
                  placeholder="Enter product url"
                  value={product}
                  onChange={(e) => handleProductChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none"
                />
                {(touched.product || touched.submitAttempted) && errors.product && (
                  <p className="text-red-500 text-xs">{errors.product}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">
                  Add personalized product link
                </label>
                <input
                  type="text"
                  placeholder="Enter personalized product url"
                  value={personalProduct}
                  onChange={(e) => handlePersonalProductChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none"
                />
                {(touched.personalProduct || touched.submitAttempted) && errors.personalProduct && (
                  <p className="text-red-500 text-xs">{errors.personalProduct}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#62556B]">
                  Mantra Icon (Image) *
                </label>
                <div className="w-full border-2 border-dashed rounded-lg p-6 flex items-center justify-center transition-all border-[#9275C9] bg-[#EFEAF3]">
                  {mantraIcon ? (
                    <div className="flex items-center gap-3">
                      <span className="px-4 py-2 bg-white border rounded-lg text-sm truncate max-w-[220px]">
                        {mantraIcon.name}
                      </span>

                      <button
                        type="button"
                        onClick={handleRemoveMantraIcon}
                        className="text-red-500 font-bold hover:opacity-70"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer text-sm font-medium text-[#726779]">
                      <p className="text-center px-4 py-3 rounded-lg border border-lightPurple">
                        Choose image
                      </p>

                      <Input
                        ref={mantraIconInputRef}
                        type="file"
                        accept=".svg,.png,.jpg,.jpeg,.webp,image/*"
                        className="hidden"
                        onChange={(e) => handleMantraIconChange(e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
                {(touched.mantraIcon || touched.submitAttempted) && errors.mantraIcon && (
                  <p className="text-red-500 text-xs">{errors.mantraIcon}</p>
                )}
              </div>
            </div>
          )}

          {errors.api && <p className="text-red-500 text-sm text-center">{errors.api}</p>}

          <div className="flex justify-between sm:justify-center gap-4 sticky sm:relative bottom-0 bg-white py-2 sm:px-0 px-4">
            <button
              type="button"
              onClick={handleSecondaryAction}
              className="px-6 sm:px-12 py-2 border border-[#1A0A30] rounded-xl font-bold"
            >
              {isMantraStep ? 'Back' : 'Cancel'}
            </button>

            <button
              type="button"
              onClick={handlePrimaryAction}
              disabled={isPrimaryDisabled}
              className={`px-6 sm:px-12 py-2 rounded-xl font-bold text-white flex items-center justify-center gap-2 hover:bg-lightPurple
    ${isPrimaryDisabled ? 'bg-[#1A0A30]/70 cursor-not-allowed' : 'bg-[#1A0A30]'}`}
            >
              {!isLockedFlow || currentStep === 2 ? (
                isSubmittingAudio ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin text-xs sm:text-sm" />
                    Uploading...
                  </>
                ) : (
                  'Add'
                )
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </Modal>
    );
  }
);
